import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/checkout/return")({
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: CheckoutReturn,
  head: () => ({
    meta: [
      { title: "Payment complete — SmartEssay" },
      { name: "description", content: "Your SmartEssay subscription is being activated. Hang tight while we confirm your payment." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function CheckoutReturn() {
  const { session_id } = Route.useSearch();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"pending" | "ready" | "timeout">("pending");

  // Poll the subscriptions table for up to ~30s so we only land users on
  // /app once the webhook has written their row. Avoids a momentary
  // paywall flash.
  useEffect(() => {
    if (!session_id) {
      setStatus("ready");
      return;
    }
    let cancelled = false;
    let tries = 0;
    const env = getStripeEnvironment();

    async function check() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate({ to: "/auth" });
        return;
      }
      const { data: sub } = await supabase
        .from("subscriptions")
        .select("status,current_period_end")
        .eq("user_id", session.user.id)
        .eq("environment", env)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      const periodEnd = sub?.current_period_end ? new Date(sub.current_period_end as string).getTime() : null;
      const future = periodEnd === null || (periodEnd && periodEnd > Date.now());
      const isActive = sub && ["active", "trialing", "past_due"].includes(sub.status as string) && future;
      if (isActive) {
        if (!cancelled) setStatus("ready");
        return;
      }
      tries += 1;
      if (tries > 15) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      setTimeout(check, 2000);
    }
    check();
    return () => { cancelled = true; };
  }, [session_id, navigate]);

  return (
    <div className="grid min-h-screen place-items-center bg-hero-gradient px-6">
      <div className="max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
        {status === "pending" ? (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-primary" />
            <h1 className="mt-4 font-display text-2xl font-semibold">Finalizing your subscription…</h1>
            <p className="mt-2 text-muted-foreground">Just a moment while we activate your access.</p>
          </>
        ) : (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-primary" />
            <h1 className="mt-4 font-display text-2xl font-semibold">
              {status === "ready" ? "You're in!" : "Almost there"}
            </h1>
            <p className="mt-2 text-muted-foreground">
              {status === "ready"
                ? "Your subscription is active. Start building outlines now."
                : "Your payment went through. If access isn't visible yet, refresh in a few seconds."}
            </p>
            <Button asChild className="mt-6 w-full shadow-glow">
              <Link to="/app">Go to the app</Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
